/** @jsxRuntime classic */
/** @jsx jsx */

import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { jsx } from '@emotion/react';
import { isSameDate } from '../../../utils/timeUtils';
import { useMiddlewareContext } from '../../../hooks/useMiddlewareContext';
import { IDraggableDayToDropProps } from '../../../types';
import {
	draggableContainerHorizontal,
	draggableContentHorizontal,
	draggableHandler,
	draggableResizeHandler,
} from '../../styles';
import { DndIcon } from '../../../utils/DndIcon';
import { ResizeIcon } from '../../../utils/ResizeIcon';
import { MIDNIGHT } from '../../../../lib/timeUtils';
import { Resizable } from 'react-resizable';
import { getResizeConstraints, useResize } from '../hooks/useResize';
import { ResourceNameTypeEnum } from '../../../../types/enums';

export const DraggableHorizontal: React.FC<IDraggableDayToDropProps> = ({
	children,
	id,
	state,
	parentId,
	groupTop,
	groupCount,
	groupIndex,
	isEditable,
	isConnectedMaintenance = false,
	currentDate,
	setDraggableSlot,
	updateSlotData,
}) => {
	const elementId = `draggable-${id}` as const;

	const { options } = useMiddlewareContext();

	isEditable &&= options.draggingOptions?.[state.event?.parentType as ResourceNameTypeEnum];

	const { isDragging, setNodeRef, listeners } = useDraggable({
		id: String(id),
		data: {
			parentId: parentId,
			event: state,
		},
	});

	const { ref, onResizeEnd, onResize, top, size } = useResize({ state, currentDate, parentId, horizontal: true });

	const isResizable: boolean =
		Boolean(options.dragAndDrop) &&
		(options.resize?.includes(state.slotType) || false) &&
		isEditable! &&
		!isDragging &&
		(isSameDate(state.endDate, currentDate) || state.endTime === MIDNIGHT);

	return (
		<div
			id={elementId}
			css={draggableContainerHorizontal(top - (groupTop || 0), groupCount, groupIndex)}
			ref={setNodeRef}
		>
			<Resizable
				axis={isResizable ? 'x' : 'none'}
				width={size}
				onResize={(e, data) => onResize(e as any, data, setDraggableSlot)}
				onResizeStop={(e, data) => onResizeEnd(updateSlotData)}
				minConstraints={getResizeConstraints(options.dndStepLength)}
				handle={
					isResizable && (
						<div css={draggableResizeHandler('ew-resize')}>
							<ResizeIcon />
						</div>
					)
				}
			>
				<div data-aid={`horizontal-draggable-content-${state.id}`} css={draggableContentHorizontal(size)} ref={ref}>
					{children}
					{options.dragAndDrop && isEditable && (
						<div {...listeners} css={draggableHandler}>
							<DndIcon />
						</div>
					)}
				</div>
			</Resizable>
		</div>
	);
};
